import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, RefreshCw, Trophy } from 'lucide-react';
import { useTechAuth } from '@/contexts/TechAuthContext';
import {
  getTechBadges,
  getTechContests,
  getTechLeaderboard,
  TechBadge,
  TechBadgesResponse,
  TechContestsResponse,
  TechLeaderboardRange,
  TechLeaderboardResponse,
  TechLeaderboardRow,
} from '@/lib/tech-api-client';

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error || 'Unknown error');
}

function formatPercent(value: number | null): string {
  if (value === null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 0 }).format(value);
}

function formatUsd(value: number | null): string {
  if (value === null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

function Chip({ label, className }: { label: string; className: string }) {
  return (
    <span className={['inline-flex items-center px-2.5 py-1 rounded-full border text-xs font-medium', className].join(' ')}>
      {label}
    </span>
  );
}

function RangeButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'px-3 py-2 rounded-xl text-sm font-medium border transition-colors',
        active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50',
      ].join(' ')}
    >
      {label}
    </button>
  );
}

type LeaderboardMetric = 'sales' | 'close_rate' | 'retention';

function MetricButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'px-3 py-2 rounded-xl text-sm font-medium border transition-colors',
        active ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50',
      ].join(' ')}
    >
      {label}
    </button>
  );
}

function formatBadge(badge: TechBadge): { label: string; className: string } {
  if (badge.earned) return { label: badge.title, className: 'bg-green-100 text-green-800 border-green-200' };
  return { label: badge.title, className: 'bg-gray-100 text-gray-700 border-gray-200' };
}

export default function TechLeaderboardPage() {
  const { accessToken, technician } = useTechAuth();

  const [range, setRange] = useState<TechLeaderboardRange>('weekly');
  const [metric, setMetric] = useState<LeaderboardMetric>('sales');

  const [leaderboard, setLeaderboard] = useState<TechLeaderboardResponse | null>(null);
  const [badges, setBadges] = useState<TechBadgesResponse | null>(null);
  const [contests, setContests] = useState<TechContestsResponse | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    if (!accessToken) return;

    setLoading(true);
    setError(null);

    try {
      const [lb, bd, ct] = await Promise.all([
        getTechLeaderboard(accessToken, range),
        getTechBadges(accessToken),
        getTechContests(accessToken),
      ]);
      setLeaderboard(lb);
      setBadges(bd);
      setContests(ct);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, range]);

  const rows = useMemo(() => leaderboard?.rows || [], [leaderboard?.rows]);

  const sortedRows = useMemo(() => {
    const copy = rows.slice();

    if (metric === 'sales') {
      copy.sort((a, b) => {
        if (b.memberships_sold !== a.memberships_sold) return b.memberships_sold - a.memberships_sold;
        return String(a.technician.full_name || '').localeCompare(String(b.technician.full_name || ''));
      });
      return copy;
    }

    if (metric === 'close_rate') {
      copy.sort((a, b) => {
        const aRate = a.close_rate ?? -1;
        const bRate = b.close_rate ?? -1;
        if (bRate !== aRate) return bRate - aRate;
        if (b.presentations !== a.presentations) return b.presentations - a.presentations;
        return String(a.technician.full_name || '').localeCompare(String(b.technician.full_name || ''));
      });
      return copy;
    }

    copy.sort((a, b) => {
      const aRate = a.retention.rate ?? -1;
      const bRate = b.retention.rate ?? -1;
      if (bRate !== aRate) return bRate - aRate;
      if (b.retention.total !== a.retention.total) return b.retention.total - a.retention.total;
      return String(a.technician.full_name || '').localeCompare(String(b.technician.full_name || ''));
    });
    return copy;
  }, [metric, rows]);

  const earnedBadges = useMemo(() => (badges?.badges || []).filter((b) => b.earned), [badges?.badges]);
  const activeContests = useMemo(() => contests?.contests || [], [contests?.contests]);

  const currentTechId = technician?.id ? String(technician.id) : null;

  const metricHint =
    metric === 'sales'
      ? 'Ranked by memberships sold (new sales only).'
      : metric === 'close_rate'
        ? 'Close rate requires at least 10 presentations in the selected range.'
        : 'Retention rate is based on all-time memberships sold that are still active.';

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-yellow-500 flex items-center justify-center text-white">
              <Trophy className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Leaderboard</h1>
              <p className="text-gray-600 mt-1">{metricHint}</p>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50"
          disabled={loading}
        >
          <RefreshCw className={['w-4 h-4', loading ? 'animate-spin' : ''].join(' ')} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="p-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-2xl">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800 font-medium">{error}</p>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-2xl p-5">
        <h2 className="text-lg font-semibold text-gray-900">Monthly contests</h2>
        <p className="text-sm text-gray-600 mt-1">Active competitions set by the office. Track your progress in real time.</p>

        {activeContests.length === 0 ? (
          <div className="mt-4 text-sm text-gray-500">No active contests right now.</div>
        ) : (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {activeContests.map((contest) => {
              const goalTypeLabel =
                contest.goal_type === 'first_to'
                  ? 'First to goal'
                  : contest.goal_type === 'highest_rate'
                    ? 'Highest close rate'
                    : 'Most sales';

              const goalLabel = contest.goal_value ? `Goal: ${contest.goal_value}` : 'Goal: —';

              const myValueLabel =
                contest.metric_key === 'close_rate'
                  ? formatPercent(contest.my.value)
                  : contest.my.value === null
                    ? '—'
                    : String(contest.my.value);

              const leaderValueLabel =
                contest.leader?.value === null || contest.leader?.value === undefined
                  ? '—'
                  : contest.metric_key === 'close_rate'
                    ? formatPercent(contest.leader.value)
                    : String(contest.leader.value);

              const showProgress = contest.metric_key === 'sales' && contest.goal_value && contest.goal_value > 0;
              const progressValue = showProgress
                ? Math.min(contest.goal_value || 0, Math.max(0, Number(contest.my.value || 0)))
                : 0;

              return (
                <div key={contest.id} className="border border-gray-200 rounded-2xl p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold text-gray-900">{contest.name}</div>
                      <div className="mt-1 text-xs text-gray-500">
                        {goalTypeLabel} • {goalLabel}
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        {contest.start_date} → {contest.end_date}
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="text-xs text-gray-500">Prize</div>
                      <div className="text-sm font-semibold text-gray-900">
                        {contest.prize_amount !== null ? formatUsd(contest.prize_amount) : contest.prize_description || '—'}
                      </div>
                    </div>
                  </div>

                  {contest.description && <div className="mt-3 text-sm text-gray-700">{contest.description}</div>}

                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="rounded-xl bg-gray-50 border border-gray-100 p-3">
                      <div className="text-xs text-gray-500">You</div>
                      <div className="mt-1 text-lg font-semibold text-gray-900">{myValueLabel}</div>
                      <div className="text-xs text-gray-500 mt-1">Rank: {contest.my.rank ? `#${contest.my.rank}` : '—'}</div>
                    </div>
                    <div className="rounded-xl bg-gray-50 border border-gray-100 p-3">
                      <div className="text-xs text-gray-500">Leader</div>
                      <div className="mt-1 text-sm font-semibold text-gray-900">
                        {contest.leader?.full_name || '—'}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{leaderValueLabel}</div>
                    </div>
                  </div>

                  {showProgress && (
                    <div className="mt-4">
                      <progress
                        value={progressValue}
                        max={contest.goal_value || 1}
                        className="w-full h-3"
                      />
                      <div className="mt-1 text-xs text-gray-500">
                        {progressValue}/{contest.goal_value}
                      </div>
                    </div>
                  )}

                  {contest.top.length > 0 && (
                    <div className="mt-4">
                      <div className="text-xs text-gray-500">Top standings</div>
                      <div className="mt-2 space-y-1">
                        {contest.top.map((row) => {
                          const valueLabel =
                            row.value === null
                              ? '—'
                              : contest.metric_key === 'close_rate'
                                ? formatPercent(row.value)
                                : String(row.value);
                          return (
                            <div key={row.technician.id} className="flex items-center justify-between text-sm">
                              <div className="text-gray-700">
                                #{row.rank} {row.technician.full_name}
                              </div>
                              <div className="font-medium text-gray-900">{valueLabel}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            <RangeButton label="This week" active={range === 'weekly'} onClick={() => setRange('weekly')} />
            <RangeButton label="This month" active={range === 'monthly'} onClick={() => setRange('monthly')} />
            <RangeButton label="Year to date" active={range === 'ytd'} onClick={() => setRange('ytd')} />
          </div>

          <div className="flex flex-wrap gap-2">
            <MetricButton label="Sales" active={metric === 'sales'} onClick={() => setMetric('sales')} />
            <MetricButton label="Close Rate" active={metric === 'close_rate'} onClick={() => setMetric('close_rate')} />
            <MetricButton label="Retention" active={metric === 'retention'} onClick={() => setMetric('retention')} />
          </div>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-2 pr-4">Rank</th>
                <th className="py-2 pr-4">Technician</th>
                <th className="py-2 pr-4">Sales</th>
                <th className="py-2 pr-4">Presentations</th>
                <th className="py-2 pr-4">Close rate</th>
                <th className="py-2">Retention</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-6 text-gray-500">
                    No leaderboard data yet.
                  </td>
                </tr>
              ) : (
                sortedRows.map((row, index) => {
                  const isMe = currentTechId && String(row.technician.id) === currentTechId;

                  const closeRateLabel =
                    row.close_rate === null ? '—' : `${Math.round(row.close_rate * 100)}%`;

                  const retentionLabel =
                    row.retention.rate === null
                      ? '—'
                      : `${Math.round(row.retention.rate * 100)}% (${row.retention.active}/${row.retention.total})`;

                  return (
                    <tr
                      key={row.technician.id}
                      className={[
                        'border-t border-gray-100',
                        isMe ? 'bg-blue-50' : 'hover:bg-gray-50',
                      ].join(' ')}
                    >
                      <td className="py-3 pr-4 font-medium text-gray-900">#{index + 1}</td>
                      <td className="py-3 pr-4">
                        <div className="font-medium text-gray-900">{row.technician.full_name}</div>
                        <div className="text-xs text-gray-500">{row.technician.role}</div>
                      </td>
                      <td className="py-3 pr-4">
                        <div className="font-medium text-gray-900">{row.memberships_sold}</div>
                        <div className="text-xs text-gray-500">
                          new {row.sale_type_counts.new} • upg {row.sale_type_counts.upgrade} • ren {row.sale_type_counts.renewal}
                        </div>
                      </td>
                      <td className="py-3 pr-4">{row.presentations}</td>
                      <td className="py-3 pr-4">{closeRateLabel}</td>
                      <td className="py-3">{retentionLabel}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-5">
        <h2 className="text-lg font-semibold text-gray-900">Your badges</h2>
        <p className="text-sm text-gray-600 mt-1">Badges are computed from your sales/presentations/retention.</p>

        <div className="mt-4 flex flex-wrap gap-2">
          {(badges?.badges || []).length === 0 ? (
            <div className="text-sm text-gray-500">No badges yet.</div>
          ) : (
            (badges?.badges || []).map((badge) => {
              const chip = formatBadge(badge);
              return <Chip key={badge.badge_type} label={chip.label} className={chip.className} />;
            })
          )}
        </div>

        {earnedBadges.length > 0 && (
          <div className="mt-4 text-xs text-gray-500">
            Earned: {earnedBadges.map((b) => b.title).join(', ')}
          </div>
        )}
      </div>
    </div>
  );
}
